import { LockOpen } from "@mui/icons-material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { SVG_MUI } from "@ryde/assets";
import {
  useModalConfirm,
  useRidesProcessing,
  useRideStationContext,
} from "@ryde/redux";
import {
  ConfirmResult,
  errors,
  Log,
  warnAndToastSimpleResult,
} from "@ryde/shared";
import { blueGreyMui } from "@ryde/theme";

import { AccessControl } from "../../../access-control";
import { FramerTooltip } from "../../../tooltips";

export const RideStationRowLock: FC = () => {
  const { station, rideDto, isFirstStation, isLastStation, mode } =
    useRideStationContext();

  const { t } = useTranslation();
  const { isLocked = false, id: stationId = 0 } = station || {};
  const { id: rideId = 0 } = rideDto || {};

  const { updateRideStationAttributes } = useRidesProcessing();
  const { confirmDialogResult } = useModalConfirm();

  const handleLockClick = useEvent(async (): Promise<void> => {
    const message = station?.isLocked ? t("ride.UNLOCK") : t("ride.LOCK");
    const confirmationResult = await confirmDialogResult({
      title: t("ride.CONFIRM_LOCKING_STATION"),
      message,
    });

    if (confirmationResult !== ConfirmResult.OK) {
      Log.logAndToast(t(errors.ride.lockingStationNotConfirmedByUser));
      return;
    }

    const updateResult = await updateRideStationAttributes(
      { stationId, isLocked: !isLocked },
      rideId,
      mode,
    );

    if (!updateResult.success) {
      warnAndToastSimpleResult(updateResult, t);
    } else {
      Log.debug("Lock updating finished", updateResult);
    }
  });

  return (
    <AccessControl action="update" entity="ride">
      {isLocked && (isFirstStation || isLastStation) && (
        <FramerTooltip
          content={t("ride.RemoveLockStation")}
          rtlStrippedBottomLeftPlacement
        >
          <SVG_MUI.Lock3SVG className="pointer" onClick={handleLockClick} />
        </FramerTooltip>
      )}
      {!isLocked && (isFirstStation || isLastStation) && (
        <FramerTooltip
          content={t("ride.LockStation")}
          rtlStrippedBottomLeftPlacement
        >
          <LockOpen
            className="pointer"
            sx={{ color: blueGreyMui._500, fontSize: 16 }}
            onClick={handleLockClick}
          />
        </FramerTooltip>
      )}
    </AccessControl>
  );
};
